'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { SwitchProps as AriaSwitchProps } from 'react-aria-components';
import { Switch as AriaSwitch } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { ToggleGroup } from './ToggleGroup';

const SEMANTIC_NAME_BASE = 'RomaToggleBase';
const SEMANTIC_NAME = 'RomaToggle';

export interface ToggleBaseProps {
    size?: 'xs' | 'sm' | 'md';
    className?: string;
    isHovered?: boolean;
    isFocusVisible?: boolean;
    isSelected?: boolean;
    isDisabled?: boolean;
}

export const ToggleBase = ({
    className,
    isHovered,
    isDisabled,
    isFocusVisible,
    isSelected,
    size = 'sm',
}: ToggleBaseProps) => {
    const sizes = {
        xs: {
            root: 'h-4 w-7 p-0.5',
            switch: cx('size-3', isSelected && 'translate-x-3'),
        },
        sm: {
            root: 'h-5 w-9 p-0.5',
            switch: cx('size-4', isSelected && 'translate-x-4'),
        },
        md: {
            root: 'h-6 w-11 p-0.5',
            switch: cx('size-5', isSelected && 'translate-x-5'),
        },
    };

    return (
        <div
            className={cx(
                SEMANTIC_NAME_BASE,
                'shrink-0 cursor-pointer rounded-full bg-tertiary',
                'transition duration-160 ease-linear',
                isSelected && 'bg-success-solid',
                isSelected && isHovered && 'bg-success-700',
                isDisabled && 'cursor-default bg-disabled',
                isFocusVisible && 'outline-focus-ring outline-2 -outline-offset-1',
                sizes[size].root,
                className,
            )}
        >
            <div
                className={cx(
                    'rounded-full bg-fg-white shadow-sm',
                    'transition-transform duration-160 ease-in-out',
                    isDisabled && 'bg-toggle-button-fg_disabled',
                    sizes[size].switch,
                )}
            />
        </div>
    );
};
ToggleBase.displayName = 'RomaToggleBase';

export interface ToggleProps extends AriaSwitchProps {
    size?: 'xs' | 'sm' | 'md';
    toggleSize?: 'xs' | 'sm' | 'md';
    label?: string;
    hint?: ReactNode;
    align?: 'left' | 'right';
}

const styles = {
    sizes: {
        xs: {
            root: 'gap-2',
            textWrapper: '',
            label: 'text-sm font-medium',
            hint: 'text-sm',
            toggleBase: 'mt-0.5',
        },
        sm: {
            root: 'gap-2',
            textWrapper: '',
            label: 'text-sm font-medium',
            hint: 'text-sm',
            toggleBase: '',
        },
        md: {
            root: 'gap-3',
            textWrapper: 'gap-0.5',
            label: 'text-md font-medium',
            hint: 'text-sm',
            toggleBase: '',
        },
    },
    align: {
        left: '',
        right: 'flex-row-reverse justify-between',
    },
};

const ToggleRoot = ({
    label,
    hint,
    className,
    toggleSize,
    size = 'sm',
    align = 'right',
    ...ariaSwitchProps
}: ToggleProps) => {
    return (
        <AriaSwitch
            {...ariaSwitchProps}
            className={(renderProps) =>
                cx(
                    SEMANTIC_NAME,
                    'flex items-start',
                    renderProps.isDisabled && 'cursor-default',
                    styles.sizes[size].root,
                    styles.align[align],
                    typeof className === 'function' ? className(renderProps) : className,
                )
            }
        >
            {({ isSelected, isDisabled, isFocusVisible, isHovered }) => (
                <>
                    <ToggleBase
                        size={toggleSize ?? size}
                        isHovered={isHovered}
                        isDisabled={isDisabled}
                        isFocusVisible={isFocusVisible}
                        isSelected={isSelected}
                        className={styles.sizes[size].toggleBase}
                    />

                    {(label || hint) && (
                        <div className={cx('flex flex-col', styles.sizes[size].textWrapper)}>
                            {label && (
                                <p
                                    className={cx(
                                        'select-none',
                                        // when inside a toggle group, follow the "hint" styles if no hint is provided
                                        !hint && 'in-data-toggle-group:text-tertiary in-data-toggle-group:font-normal',
                                        styles.sizes[size].label,
                                    )}
                                >
                                    {label}
                                </p>
                            )}
                            {hint && (
                                <span
                                    className={cx('text-tertiary font-normal', styles.sizes[size].hint)}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    {hint}
                                </span>
                            )}
                        </div>
                    )}
                </>
            )}
        </AriaSwitch>
    );
};

ToggleRoot.displayName = 'RomaToggle';

export const Toggle: typeof ToggleRoot & { Base: typeof ToggleBase; Group: typeof ToggleGroup } = Object.assign(
    ToggleRoot,
    {
        Base: ToggleBase,
        Group: ToggleGroup,
    },
);
